// Swiper Settings -- client store of the platform defaults
// (GET /swiper-settings/public). Loaded once per page load, shared by every
// managed Swiper through useSyncExternalStore; the last known value is kept
// in localStorage so the next visit renders with it immediately (a per-viewer
// convenience only -- the server copy is always refetched and wins).
//
// The Swiper Settings panel can also set a DRAFT for one component: only the
// panel's own preview reads it (never the live site), so unsaved changes are
// never applied anywhere else.
import { elakay_API } from "../../../config";
import { sanitizePerDevice, COMPONENT_BY_ID } from "./swiperSettingsModel";

const STORAGE_KEY = "elakay:swiperDefaults:v1";

const readCache = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeCache = (components) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(components));
  } catch { /* private mode / quota: the network copy still works */ }
};

// Only known components, each sanitized with its own restrictions.
export const sanitizeDefaultsPayload = (components) => {
  const out = {};
  if (!components || typeof components !== "object") return out;
  Object.entries(components).forEach(([componentId, config]) => {
    const component = COMPONENT_BY_ID[componentId];
    if (!component) return;
    const clean = sanitizePerDevice(config, component);
    if (Object.keys(clean).length) out[componentId] = clean;
  });
  return out;
};

let state = { status: "idle", components: sanitizeDefaultsPayload(readCache()), updatedAt: null };
const listeners = new Set();

const emit = () => listeners.forEach((listener) => listener());

export const subscribeSwiperDefaults = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const getSwiperDefaultsSnapshot = () => state;

export const loadSwiperDefaults = ({ force = false } = {}) => {
  if (!force && (state.status === "loading" || state.status === "ready")) return;
  if (typeof fetch !== "function" || !elakay_API) {
    state = { ...state, status: "error" };
    return;
  }
  state = { ...state, status: "loading" };
  Promise.resolve()
    .then(() => fetch(`${elakay_API}/swiper-settings/public`, { headers: { Accept: "application/json" } }))
    .then((response) => (response.ok ? response.json() : Promise.reject(new Error(`HTTP ${response.status}`))))
    .then((data) => {
      const components = sanitizeDefaultsPayload(data?.components);
      writeCache(components);
      state = { status: "ready", components, updatedAt: data?.updatedAt || null };
      emit();
    })
    .catch(() => {
      // Keep the cached/origin values; a later mount may retry.
      state = { ...state, status: "error" };
      emit();
    });
};

// After a successful save / reset in the panel: the whole app reflects the
// new defaults immediately, without a reload.
export const setSwiperDefaultsLocally = (componentId, config) => {
  const components = { ...state.components };
  const component = COMPONENT_BY_ID[componentId];
  const clean = component ? sanitizePerDevice(config, component) : {};
  if (Object.keys(clean).length) components[componentId] = clean;
  else delete components[componentId];
  writeCache(components);
  state = { ...state, components };
  emit();
};

// After a Global Swiper Settings apply / save: the server returns the whole
// effective payload (inherited global values included).
export const replaceSwiperDefaultsLocally = (components) => {
  const clean = sanitizeDefaultsPayload(components);
  writeCache(clean);
  state = { ...state, components: clean };
  emit();
};

// Test helper.
export const __resetSwiperDefaultsStore = (components = {}) => {
  state = { status: "ready", components: sanitizeDefaultsPayload(components), updatedAt: null };
  emit();
};
